import { ErrorBody } from '../wire';
import type { ErrorCode } from '@yourstore/domain';

/**
 * What went wrong, and — the question the outbox actually asks — **is trying
 * again a sane thing to do?**
 *
 * Spec §8.3 splits every failed intent into exactly two piles. One is "the
 * network or the server is having a moment": keep the intent, wait, replay it.
 * The other is "the server has ruled on this": stop, and raise it as a task for
 * the owner. Getting a member of the second pile into the first is a device that
 * retries a rejected booking forever; getting a member of the first into the
 * second is a task list full of tunnels and Wi-Fi hiccups. So the split lives in
 * one predicate, named, with the reasoning next to it.
 */

/** `offline` is ours: the request never reached a server to be answered. */
export type FailureCode = ErrorCode | 'offline' | 'malformed';

export class ApiFailure extends Error {
  readonly code: FailureCode;
  readonly status: number;
  readonly detail: Record<string, unknown> | undefined;
  /** The raw body, kept verbatim so a task can render what the server said. */
  readonly body: unknown;

  constructor(
    code: FailureCode,
    message: string,
    options: { status?: number; detail?: Record<string, unknown>; body?: unknown } = {},
  ) {
    super(message);
    this.name = 'ApiFailure';
    this.code = code;
    this.status = options.status ?? 0;
    this.detail = options.detail;
    this.body = options.body;
  }

  static offline(cause: string): ApiFailure {
    return new ApiFailure('offline', 'The device is offline.', { detail: { cause } });
  }
}

/**
 * Retryable, and why each one is on the side it is on.
 *
 * - `offline` — nothing was decided; the intent has not been seen.
 * - `unavailable` (5xx) and `rate_limited` (429) — the server said "not now",
 *   which is a different sentence from "no".
 * - `unauthenticated` (401) — **queued, not rejected.** A token expires in an
 *   hour and an iPad can be in a basement for longer. Turning an expired session
 *   into a rejected booking would lose the owner's work to a problem that fixes
 *   itself the moment somebody signs in again; the client forces one token
 *   refresh first, and if that still fails the intent waits.
 *
 * Everything else is a ruling: `invalid` (including the idempotency-key reuse
 * `400`), `forbidden` (the device was re-provisioned for another tenant),
 * `not_found` (the appointment is gone), `conflict` (spec §8.3's own case), and
 * `unsupported` (the module was switched off). Each becomes a task.
 */
export function isRetryable(failure: ApiFailure): boolean {
  return (
    failure.code === 'offline' ||
    failure.code === 'unavailable' ||
    failure.code === 'rate_limited' ||
    failure.code === 'unauthenticated' ||
    failure.status === 408 ||
    // A body we could not parse from a 2xx is a bug, but not one the owner can
    // act on, and the write may well have landed. Retrying is safe precisely
    // because the intent is idempotent.
    failure.code === 'malformed'
  );
}

/** Turn a non-2xx response body into the failure it describes. */
export function failureFromBody(status: number, body: unknown): ApiFailure {
  const parsed = ErrorBody.safeParse(body);
  if (parsed.success) {
    return new ApiFailure(parsed.data.error.code, parsed.data.error.message, {
      status,
      detail: parsed.data.error.detail,
      body,
    });
  }
  // "Never a 200 with an error inside" is `core-api`'s promise, not the
  // proxy's. An HTML error page from something in front of it still has to
  // become one of our codes.
  return new ApiFailure(status >= 500 ? 'unavailable' : 'invalid', `HTTP ${status}.`, {
    status,
    body,
  });
}
